import { Head, router } from '@inertiajs/react'
import { useState } from 'react'
import AdminLayout from '../../components/layout/AdminLayout'
import StatsCard from '../../components/dashboard/StatsCard'
import BarChart from '../../components/charts/BarChart'
import LineChart from '../../components/charts/LineChart'
import PieChart from '../../components/charts/PieChart'

export default function BillingIndex({
  report,
  filters,
  agencies,
  lead_types_options
}) {
  const [selectedPeriod, setSelectedPeriod] = useState(filters.period || 'current_month')

  const handleFilterChange = (period) => {
    setSelectedPeriod(period)
    router.get('/billing', { period }, { preserveState: true })
  }

  // Préparer les données pour les graphiques
  const evolutionData = report.monthly_evolution.map(month => ({
    name: month.month_short,
    value: month.total
  }))

  const distributionData = report.by_type.reduce((acc, item) => {
    acc[item.type_label] = item.total_cost
    return acc
  }, {})

  const distributionColors = {
    'Vendeur': '#EF4444',
    'Bailleur': '#F59E0B',
    'Acheteur': '#3B82F6',
    'Locataire': '#10B981'
  }

  return (
    <AdminLayout>
      <Head title="Facturation" />

      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Facturation</h1>
            <p className="text-gray-600 mt-1">
              Reporting et coûts par type de lead
            </p>
          </div>

          <div className="flex gap-2">
            {['current_month', 'last_month', 'quarter', 'year'].map(period => (
              <button
                key={period}
                onClick={() => handleFilterChange(period)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedPeriod === period
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {period === 'current_month' && 'Mois'}
                {period === 'last_month' && 'Mois précédent'}
                {period === 'quarter' && 'Trimestre'}
                {period === 'year' && 'Année'}
              </button>
            ))}
          </div>
        </div>

        {/* Résumé KPI */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <StatsCard
            title="Total leads"
            value={report.summary.total_leads}
            subtitle="Leads générés"
            icon="users"
            color="blue"
          />
          <StatsCard
            title="Coût total"
            value={`${report.summary.total_cost.toFixed(2)}€`}
            subtitle="Période sélectionnée"
            icon="currency"
            color="green"
          />
          <StatsCard
            title="Coût moyen"
            value={`${report.summary.average_cost.toFixed(2)}€`}
            subtitle="Par lead"
            icon="chart"
            color="purple"
          />
        </div>

        {/* Tableau par type */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Détail par type de lead</h2>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Quantité
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Prix de base
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Coût total
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Coût moyen
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      % Total
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {report.by_type.map((item) => (
                    <tr key={item.type}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {item.type_label}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {item.count}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {item.base_price.toFixed(2)}€
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                        {item.total_cost.toFixed(2)}€
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {item.average_cost.toFixed(2)}€
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {item.percentage}%
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-gray-50 font-bold">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      TOTAL
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {report.summary.total_leads}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      -
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {report.summary.total_cost.toFixed(2)}€
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {report.summary.average_cost.toFixed(2)}€
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      100%
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Graphiques */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Évolution mensuelle */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Évolution des coûts (6 derniers mois)</h2>
            {evolutionData.length > 0 ? (
              <LineChart
                data={evolutionData}
                color="#3B82F6"
                label="Coût total (€)"
              />
            ) : (
              <p className="text-gray-500 text-center py-8">Aucune donnée disponible</p>
            )}
          </div>

          {/* Répartition par type */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Répartition par type</h2>
            {Object.keys(distributionData).length > 0 ? (
              <PieChart
                data={distributionData}
                colors={Object.values(distributionColors)}
              />
            ) : (
              <p className="text-gray-500 text-center py-8">Aucune donnée disponible</p>
            )}
          </div>
        </div>

        {/* Détail des leads (100 premiers) */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Détail des leads ({report.leads_detail.length})</h2>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Lead
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Score
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Prix base
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Multiplic.
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Prix final
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {report.leads_detail.map((lead) => (
                    <tr key={lead.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {lead.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {lead.type_label}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {lead.score}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {lead.base_price.toFixed(2)}€
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {lead.multiplier}x
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                        {lead.final_price.toFixed(2)}€
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {lead.created_at}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {report.leads_detail.length >= 100 && (
              <p className="text-sm text-gray-500 mt-4 text-center">
                Affichage des 100 premiers leads. Utilisez les filtres pour affiner la recherche.
              </p>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  )
}
